// interaction/stores/navigation.store.js — Navigation client store
//
// Holds navigation slice (currentStep, highestStep) in memory.
// Synchronized to state.js via legacy/navigation-bridge.js (PR-17).
//
// @module

/** @type {{ currentStep: number, highestStep: number }} */
const nav = {
  currentStep: 1,
  highestStep: 1,
};

/** @type {Array<function>} */
const listeners = [];

/**
 * Return an immutable snapshot of current navigation state.
 * @returns {{ currentStep: number, highestStep: number }}
 */
export function getSnapshot() {
  return { ...nav };
}

/**
 * Replace navigation fields in bulk.
 * Only overwrites keys present in `partial`.
 * @param {Partial<{ currentStep: number, highestStep: number }>} partial
 */
export function replaceNavigation(partial) {
  if (!partial || typeof partial !== 'object') return;
  let changed = false;
  for (const key of Object.keys(partial)) {
    if (key in nav && nav[key] !== partial[key]) {
      nav[key] = partial[key];
      changed = true;
    }
  }
  if (changed) notify();
}

/**
 * Bump highestStep if the given step is greater than current.
 * Returns true if highestStep was updated.
 * @param {number} step
 * @returns {boolean}
 */
export function bumpHighestStep(step) {
  if (step <= nav.highestStep) return false;
  nav.highestStep = step;
  notify();
  return true;
}

/**
 * Set currentStep.
 * @param {number} step
 */
export function setCurrentStep(step) {
  if (nav.currentStep === step) return;
  nav.currentStep = step;
  notify();
}

/**
 * Subscribe to navigation changes.
 * @param {function} listener - Called on each change.
 * @returns {function} unsubscribe
 */
export function subscribe(listener) {
  listeners.push(listener);
  return () => {
    const idx = listeners.indexOf(listener);
    if (idx !== -1) listeners.splice(idx, 1);
  };
}

function notify() {
  for (const fn of listeners) {
    try { fn(); } catch (e) { console.error('[navigation.store] listener error:', e); }
  }
}
